import React, { useState, useEffect, useCallback } from 'react';
import { Link } from 'react-router-dom';
import { useForm, useFieldArray, Controller } from 'react-hook-form';
import { yupResolver } from '@hookform/resolvers/yup';
import * as yup from 'yup';
import toast from 'react-hot-toast';
import clsx from 'clsx';
import {
  PlusIcon,
  SparklesIcon,
  MagnifyingGlassIcon,
  PencilSquareIcon,
  TrashIcon,
  ChevronLeftIcon,
  ChevronRightIcon,
  FunnelIcon,
  TagIcon,
} from '@heroicons/react/24/outline';

import {
  listQuestions,
  createQuestion,
  updateQuestion,
  deleteQuestion,
  getCategories,
} from '../../api/questionApi';
import Badge from '../../components/ui/Badge';
import Card from '../../components/ui/Card';
import Modal from '../../components/ui/Modal';
import Spinner from '../../components/ui/Spinner';
import EmptyState from '../../components/ui/EmptyState';

// ── Schema ──────────────────────────────────────────────────────────────────
const questionSchema = yup.object({
  question_text: yup.string().trim().required('Question text is required').min(10, 'Too short (min 10 chars)'),
  question_type: yup
    .string()
    .oneOf(['short_text', 'long_text', 'mcq', 'scenario', 'rating'])
    .required('Type is required'),
  category_id: yup.string().nullable(),
  difficulty_level: yup
    .string()
    .oneOf(['basic', 'moderate', 'deep'])
    .required('Difficulty is required'),
  is_required: yup.boolean(),
  tags: yup.string(),
});

// ── Helpers ──────────────────────────────────────────────────────────────────
const TABS = [
  { key: 'all',          label: 'All' },
  { key: 'mine',         label: 'My Questions' },
  { key: 'predefined',   label: 'Predefined' },
  { key: 'ai_generated', label: 'AI Generated' },
];

const TYPE_LABELS = {
  short_text: 'Short Text',
  long_text:  'Long Text',
  mcq:        'MCQ',
  scenario:   'Scenario',
  rating:     'Rating 1–10',
};

const TYPE_VARIANT = {
  short_text: 'primary',
  long_text:  'secondary',
  mcq:        'info',
  scenario:   'default',
  rating:     'warning',
};

const DIFF_VARIANT = {
  basic:    'success',
  moderate: 'warning',
  deep:     'error',
};

function useDebounce(value, delay = 400) {
  const [debounced, setDebounced] = useState(value);
  useEffect(() => {
    const t = setTimeout(() => setDebounced(value), delay);
    return () => clearTimeout(t);
  }, [value, delay]);
  return debounced;
}

// ── MCQ Options sub-form ─────────────────────────────────────────────────────
function McqOptionsEditor({ control, register, watch }) {
  const { fields, append, remove } = useFieldArray({ control, name: 'mcq_options' });
  watch('mcq_correct_index'); // used to re-render when correct option changes

  return (
    <div className="mt-4 space-y-2">
      <div className="flex items-center justify-between">
        <span className="text-sm font-medium text-neutral-700">Answer Options</span>
        <button
          type="button"
          onClick={() => append({ text: '' })}
          className="inline-flex items-center gap-1 rounded-button px-2 py-1 text-xs font-medium text-primary-600 hover:bg-primary-50 transition"
        >
          <PlusIcon className="h-3.5 w-3.5" />
          Add Option
        </button>
      </div>
      {fields.length === 0 && (
        <p className="text-xs text-neutral-400">No options yet. Add at least two.</p>
      )}
      {fields.map((field, idx) => (
        <div key={field.id} className="flex items-center gap-2">
          <Controller
            control={control}
            name="mcq_correct_index"
            render={({ field: radioField }) => (
              <input
                type="radio"
                className="h-4 w-4 cursor-pointer accent-primary-600"
                checked={Number(radioField.value) === idx}
                onChange={() => radioField.onChange(idx)}
                title="Mark as correct"
              />
            )}
          />
          <input
            {...register(`mcq_options.${idx}.text`)}
            placeholder={`Option ${idx + 1}`}
            className="flex-1 rounded-input border border-neutral-200 px-3 py-1.5 text-sm focus:border-primary-400 focus:outline-none focus:ring-2 focus:ring-primary-100"
          />
          <button
            type="button"
            onClick={() => remove(idx)}
            className="rounded p-1 text-neutral-400 hover:text-error-500 transition"
          >
            <TrashIcon className="h-4 w-4" />
          </button>
        </div>
      ))}
      <p className="text-xs text-neutral-400">Select the radio button next to the correct answer.</p>
    </div>
  );
}

// ── Question Form Modal ───────────────────────────────────────────────────────
function QuestionFormModal({ isOpen, onClose, editTarget, categories, onSuccess }) {
  const isEdit = !!editTarget;

  const {
    register,
    handleSubmit,
    watch,
    control,
    reset,
    formState: { errors, isSubmitting },
  } = useForm({
    resolver: yupResolver(questionSchema),
    defaultValues: {
      question_text: '',
      question_type: 'long_text',
      category_id: '',
      difficulty_level: 'moderate',
      is_required: false,
      tags: '',
      mcq_options: [],
      mcq_correct_index: 0,
    },
  });

  const questionType = watch('question_type');

  // Populate form when editing
  useEffect(() => {
    if (isOpen) {
      if (editTarget) {
        reset({
          question_text:     editTarget.question_text ?? '',
          question_type:     editTarget.question_type ?? 'long_text',
          category_id:       editTarget.category_id   ?? '',
          difficulty_level:  editTarget.difficulty_level ?? 'moderate',
          is_required:       editTarget.is_required ?? false,
          tags:              Array.isArray(editTarget.tags)
                               ? editTarget.tags.join(', ')
                               : (editTarget.tags ?? ''),
          mcq_options:       editTarget.mcq_options ?? [],
          mcq_correct_index: editTarget.mcq_correct_index ?? 0,
        });
      } else {
        reset({
          question_text: '',
          question_type: 'long_text',
          category_id: '',
          difficulty_level: 'moderate',
          is_required: false,
          tags: '',
          mcq_options: [],
          mcq_correct_index: 0,
        });
      }
    }
  }, [isOpen, editTarget, reset]);

  const onSubmit = async (values) => {
    const payload = {
      question_text:    values.question_text,
      question_type:    values.question_type,
      category_id:      values.category_id || null,
      difficulty_level: values.difficulty_level,
      is_required:      values.is_required,
      tags: values.tags
        ? values.tags.split(',').map((t) => t.trim()).filter(Boolean)
        : [],
    };

    if (values.question_type === 'mcq') {
      const correctIdx = Number(values.mcq_correct_index);
      payload.options = values.mcq_options.map((o, i) => ({
        label:      String.fromCharCode(65 + i), // A, B, C, D…
        text:       o.text,
        sort_order: i + 1,
        is_correct: i === correctIdx,
      }));
    }

    try {
      if (isEdit) {
        await updateQuestion(editTarget.id, payload);
        toast.success('Question updated');
      } else {
        await createQuestion(payload);
        toast.success('Question created');
      }
      onSuccess();
      onClose();
    } catch (err) {
      const msg = err?.response?.data?.message ?? 'Failed to save question';
      toast.error(msg);
    }
  };

  return (
    <Modal
      isOpen={isOpen}
      onClose={onClose}
      title={isEdit ? 'Edit Question' : 'New Question'}
      size="xl"
    >
      <form onSubmit={handleSubmit(onSubmit)} className="space-y-4">
        {/* Question Text */}
        <div>
          <label className="mb-1 block text-sm font-medium text-neutral-700">
            Question Text <span className="text-error-500">*</span>
          </label>
          <textarea
            {...register('question_text')}
            rows={3}
            placeholder="Enter your question..."
            className={clsx(
              'w-full rounded-input border px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-primary-100',
              errors.question_text
                ? 'border-error-400 focus:border-error-400'
                : 'border-neutral-200 focus:border-primary-400'
            )}
          />
          {errors.question_text && (
            <p className="mt-1 text-xs text-error-500">{errors.question_text.message}</p>
          )}
        </div>

        {/* Type + Difficulty row */}
        <div className="grid grid-cols-2 gap-4">
          <div>
            <label className="mb-1 block text-sm font-medium text-neutral-700">
              Question Type <span className="text-error-500">*</span>
            </label>
            <select
              {...register('question_type')}
              className="w-full rounded-input border border-neutral-200 px-3 py-2 text-sm focus:border-primary-400 focus:outline-none focus:ring-2 focus:ring-primary-100"
            >
              <option value="short_text">Short Text</option>
              <option value="long_text">Long Text / Paragraph</option>
              <option value="mcq">Multiple Choice (MCQ)</option>
              <option value="scenario">Scenario</option>
              <option value="rating">Rating (1–10)</option>
            </select>
          </div>
          <div>
            <label className="mb-1 block text-sm font-medium text-neutral-700">
              Difficulty <span className="text-error-500">*</span>
            </label>
            <select
              {...register('difficulty_level')}
              className="w-full rounded-input border border-neutral-200 px-3 py-2 text-sm focus:border-primary-400 focus:outline-none focus:ring-2 focus:ring-primary-100"
            >
              <option value="basic">Basic</option>
              <option value="moderate">Moderate</option>
              <option value="deep">Deep</option>
            </select>
          </div>
        </div>

        {/* Category */}
        <div>
          <label className="mb-1 block text-sm font-medium text-neutral-700">Category</label>
          <select
            {...register('category_id')}
            className="w-full rounded-input border border-neutral-200 px-3 py-2 text-sm focus:border-primary-400 focus:outline-none focus:ring-2 focus:ring-primary-100"
          >
            <option value="">— No category —</option>
            {categories.map((c) => (
              <option key={c.id} value={c.id}>
                {c.name}
              </option>
            ))}
          </select>
        </div>

        {/* Tags */}
        <div>
          <label className="mb-1 block text-sm font-medium text-neutral-700">
            Tags
            <span className="ml-1 text-xs text-neutral-400">(comma separated)</span>
          </label>
          <div className="relative">
            <TagIcon className="pointer-events-none absolute left-3 top-1/2 h-4 w-4 -translate-y-1/2 text-neutral-400" />
            <input
              {...register('tags')}
              placeholder="e.g. market, traction, team"
              className="w-full rounded-input border border-neutral-200 py-2 pl-9 pr-3 text-sm focus:border-primary-400 focus:outline-none focus:ring-2 focus:ring-primary-100"
            />
          </div>
        </div>

        {/* Required toggle */}
        <div className="flex items-center gap-3">
          <Controller
            control={control}
            name="is_required"
            render={({ field }) => (
              <button
                type="button"
                role="switch"
                aria-checked={field.value}
                onClick={() => field.onChange(!field.value)}
                className={clsx(
                  'relative inline-flex h-5 w-9 items-center rounded-full transition-colors focus:outline-none focus-visible:ring-2 focus-visible:ring-primary-500',
                  field.value ? 'bg-primary-600' : 'bg-neutral-200'
                )}
              >
                <span
                  className={clsx(
                    'inline-block h-3.5 w-3.5 transform rounded-full bg-white shadow transition-transform',
                    field.value ? 'translate-x-4' : 'translate-x-0.5'
                  )}
                />
              </button>
            )}
          />
          <span className="text-sm text-neutral-700">Required question</span>
        </div>

        {/* MCQ Options */}
        {questionType === 'mcq' && (
          <McqOptionsEditor control={control} register={register} watch={watch} />
        )}

        {/* Footer */}
        <div className="flex justify-end gap-3 border-t border-neutral-100 pt-4">
          <button
            type="button"
            onClick={onClose}
            className="rounded-button px-4 py-2 text-sm font-medium text-neutral-600 hover:bg-neutral-100 transition"
          >
            Cancel
          </button>
          <button
            type="submit"
            disabled={isSubmitting}
            className="inline-flex items-center gap-2 rounded-button bg-primary-600 px-5 py-2 text-sm font-semibold text-white shadow-button hover:bg-primary-700 disabled:opacity-60 transition"
          >
            {isSubmitting && <Spinner size="sm" className="text-white" />}
            {isEdit ? 'Save Changes' : 'Create Question'}
          </button>
        </div>
      </form>
    </Modal>
  );
}

// ── Delete Confirm Modal ──────────────────────────────────────────────────────
function DeleteConfirmModal({ isOpen, onClose, target, onConfirm }) {
  const [deleting, setDeleting] = useState(false);

  const handleDelete = async () => {
    setDeleting(true);
    try {
      await deleteQuestion(target.id);
      toast.success('Question deleted');
      onConfirm();
      onClose();
    } catch (err) {
      toast.error(err?.response?.data?.message ?? 'Failed to delete question');
    } finally {
      setDeleting(false);
    }
  };

  return (
    <Modal isOpen={isOpen} onClose={onClose} title="Delete Question" size="sm">
      <p className="text-sm text-neutral-600">
        Are you sure you want to delete this question? This action cannot be undone.
      </p>
      {target && (
        <p className="mt-2 rounded-input bg-neutral-50 px-3 py-2 text-sm italic text-neutral-500 line-clamp-2">
          "{target.question_text}"
        </p>
      )}
      <div className="mt-5 flex justify-end gap-3">
        <button
          onClick={onClose}
          className="rounded-button px-4 py-2 text-sm font-medium text-neutral-600 hover:bg-neutral-100 transition"
        >
          Cancel
        </button>
        <button
          onClick={handleDelete}
          disabled={deleting}
          className="inline-flex items-center gap-2 rounded-button bg-error-500 px-4 py-2 text-sm font-semibold text-white hover:bg-error-600 disabled:opacity-60 transition"
        >
          {deleting && <Spinner size="sm" className="text-white" />}
          Delete
        </button>
      </div>
    </Modal>
  );
}

// ── Main Page ─────────────────────────────────────────────────────────────────
export default function QuestionLibraryPage() {
  const [questions, setQuestions]     = useState([]);
  const [categories, setCategories]   = useState([]);
  const [loading, setLoading]         = useState(true);
  const [error, setError]             = useState(null);

  const [searchRaw, setSearchRaw]     = useState('');
  const search                        = useDebounce(searchRaw, 400);
  const [categoryFilter, setCategoryFilter] = useState('');
  const [activeTab, setActiveTab]     = useState('all');

  const [page, setPage]               = useState(1);
  const [totalPages, setTotalPages]   = useState(1);
  const PER_PAGE                      = 15;

  const [modalOpen, setModalOpen]     = useState(false);
  const [editTarget, setEditTarget]   = useState(null);
  const [deleteTarget, setDeleteTarget] = useState(null);
  const [deleteOpen, setDeleteOpen]   = useState(false);

  // Load categories once
  useEffect(() => {
    getCategories()
      .then((res) => setCategories(res.data.data ?? res.data ?? []))
      .catch(() => {});
  }, []);

  // Build query params from state
  const buildParams = useCallback(() => {
    const params = { page, per_page: PER_PAGE };
    if (search)         params.search   = search;
    if (categoryFilter) params.category = categoryFilter;
    if (activeTab === 'mine')         params.scope = 'mine';
    if (activeTab === 'predefined')   params.is_predefined = true;
    if (activeTab === 'ai_generated') params.source = 'ai';
    return params;
  }, [search, categoryFilter, activeTab, page]);

  const fetchQuestions = useCallback(() => {
    setLoading(true);
    setError(null);
    listQuestions(buildParams())
      .then((res) => {
        const d = res.data;
        setQuestions(d.data ?? []);
        setTotalPages(d.meta?.last_page ?? d.last_page ?? 1);
      })
      .catch((err) => {
        setError(err?.response?.data?.message ?? 'Failed to load questions');
      })
      .finally(() => setLoading(false));
  }, [buildParams]);

  useEffect(() => {
    setPage(1);
  }, [search, categoryFilter, activeTab]);

  useEffect(() => {
    fetchQuestions();
  }, [fetchQuestions]);

  const openCreate = () => {
    setEditTarget(null);
    setModalOpen(true);
  };

  const openEdit = (q) => {
    setEditTarget(q);
    setModalOpen(true);
  };

  const openDelete = (q) => {
    setDeleteTarget(q);
    setDeleteOpen(true);
  };

  return (
    <div className="min-h-screen bg-surface p-6 lg:p-8">
      {/* Page Header */}
      <div className="mb-6 flex flex-col gap-4 sm:flex-row sm:items-center sm:justify-between">
        <div>
          <h1 className="text-2xl font-heading font-bold text-neutral-900">Question Library</h1>
          <p className="mt-0.5 text-sm text-neutral-500">
            Manage your assessment questions
          </p>
        </div>
        <div className="flex items-center gap-3">
          <Link
            to="/questions/ai"
            className="inline-flex items-center gap-2 rounded-button border border-primary-200 bg-primary-50 px-4 py-2 text-sm font-medium text-primary-700 hover:bg-primary-100 transition"
          >
            <SparklesIcon className="h-4 w-4" />
            AI Generator
          </Link>
          <button
            onClick={openCreate}
            className="inline-flex items-center gap-2 rounded-button bg-primary-600 px-4 py-2 text-sm font-semibold text-white shadow-button hover:bg-primary-700 transition"
          >
            <PlusIcon className="h-4 w-4" />
            New Question
          </button>
        </div>
      </div>

      {/* Filters */}
      <Card className="mb-6">
        <div className="flex flex-col gap-4 sm:flex-row sm:items-center">
          {/* Search */}
          <div className="relative flex-1">
            <MagnifyingGlassIcon className="pointer-events-none absolute left-3 top-1/2 h-4 w-4 -translate-y-1/2 text-neutral-400" />
            <input
              value={searchRaw}
              onChange={(e) => setSearchRaw(e.target.value)}
              placeholder="Search questions..."
              className="w-full rounded-input border border-neutral-200 py-2 pl-9 pr-3 text-sm focus:border-primary-400 focus:outline-none focus:ring-2 focus:ring-primary-100"
            />
          </div>
          {/* Category filter */}
          <div className="relative flex items-center gap-2">
            <FunnelIcon className="h-4 w-4 text-neutral-400" />
            <select
              value={categoryFilter}
              onChange={(e) => setCategoryFilter(e.target.value)}
              className="rounded-input border border-neutral-200 py-2 pl-3 pr-8 text-sm focus:border-primary-400 focus:outline-none focus:ring-2 focus:ring-primary-100"
            >
              <option value="">All Categories</option>
              {categories.map((c) => (
                <option key={c.id} value={c.id}>
                  {c.name}
                </option>
              ))}
            </select>
          </div>
        </div>
      </Card>

      {/* Tabs */}
      <div className="mb-4 flex gap-1 rounded-card border border-neutral-200 bg-white p-1 shadow-card w-fit">
        {TABS.map((tab) => (
          <button
            key={tab.key}
            onClick={() => setActiveTab(tab.key)}
            className={clsx(
              'rounded-lg px-4 py-1.5 text-sm font-medium transition',
              activeTab === tab.key
                ? 'bg-primary-600 text-white shadow-sm'
                : 'text-neutral-500 hover:text-neutral-900 hover:bg-neutral-100'
            )}
          >
            {tab.label}
          </button>
        ))}
      </div>

      {/* Table */}
      <Card>
        {loading ? (
          <div className="flex items-center justify-center py-16">
            <Spinner size="lg" className="text-primary-600" />
          </div>
        ) : error ? (
          <div className="py-12 text-center">
            <p className="text-sm text-error-500">{error}</p>
            <button
              onClick={fetchQuestions}
              className="mt-3 text-sm text-primary-600 underline hover:text-primary-700"
            >
              Retry
            </button>
          </div>
        ) : questions.length === 0 ? (
          <EmptyState
            icon={MagnifyingGlassIcon}
            title="No questions found"
            description="Try adjusting your search or filters, or create a new question."
            action={
              <button
                onClick={openCreate}
                className="inline-flex items-center gap-2 rounded-button bg-primary-600 px-4 py-2 text-sm font-semibold text-white hover:bg-primary-700 transition"
              >
                <PlusIcon className="h-4 w-4" />
                Create Question
              </button>
            }
          />
        ) : (
          <>
            <div className="overflow-x-auto">
              <table className="w-full text-sm">
                <thead>
                  <tr className="border-b border-neutral-100 text-left">
                    <th className="pb-3 pr-4 text-xs font-semibold uppercase tracking-wide text-neutral-400">
                      Question
                    </th>
                    <th className="pb-3 pr-4 text-xs font-semibold uppercase tracking-wide text-neutral-400">
                      Type
                    </th>
                    <th className="pb-3 pr-4 text-xs font-semibold uppercase tracking-wide text-neutral-400">
                      Category
                    </th>
                    <th className="pb-3 pr-4 text-xs font-semibold uppercase tracking-wide text-neutral-400">
                      Difficulty
                    </th>
                    <th className="pb-3 pr-4 text-xs font-semibold uppercase tracking-wide text-neutral-400">
                      Required
                    </th>
                    <th className="pb-3 text-xs font-semibold uppercase tracking-wide text-neutral-400">
                      Actions
                    </th>
                  </tr>
                </thead>
                <tbody className="divide-y divide-neutral-50">
                  {questions.map((q) => (
                    <tr key={q.question_id ?? q.id} className="group hover:bg-neutral-50 transition">
                      <td className="max-w-xs py-3 pr-4">
                        <p className="line-clamp-2 text-neutral-900">{q.question_text}</p>
                        {q.tags?.length > 0 && (
                          <div className="mt-1 flex flex-wrap gap-1">
                            {q.tags.slice(0, 3).map((tag) => (
                              <span
                                key={tag}
                                className="rounded-full bg-neutral-100 px-2 py-0.5 text-[10px] text-neutral-500"
                              >
                                {tag}
                              </span>
                            ))}
                          </div>
                        )}
                      </td>
                      <td className="py-3 pr-4">
                        <Badge variant={TYPE_VARIANT[q.question_type] ?? 'default'}>
                          {TYPE_LABELS[q.question_type] ?? q.question_type}
                        </Badge>
                      </td>
                      <td className="py-3 pr-4 text-neutral-600">
                        {q.category_name ?? '—'}
                      </td>
                      <td className="py-3 pr-4">
                        <Badge variant={DIFF_VARIANT[q.difficulty_level] ?? 'default'}>
                          {q.difficulty_level}
                        </Badge>
                      </td>
                      <td className="py-3 pr-4">
                        {q.is_required ? (
                          <span className="text-xs font-medium text-error-500">Required</span>
                        ) : (
                          <span className="text-xs text-neutral-400">Optional</span>
                        )}
                      </td>
                      <td className="py-3">
                        <div className="flex items-center gap-2 opacity-0 group-hover:opacity-100 transition">
                          <button
                            onClick={() => openEdit(q)}
                            className="rounded-button p-1.5 text-neutral-400 hover:bg-primary-50 hover:text-primary-600 transition"
                            title="Edit"
                          >
                            <PencilSquareIcon className="h-4 w-4" />
                          </button>
                          <button
                            onClick={() => openDelete(q)}
                            className="rounded-button p-1.5 text-neutral-400 hover:bg-error-50 hover:text-error-500 transition"
                            title="Delete"
                          >
                            <TrashIcon className="h-4 w-4" />
                          </button>
                        </div>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>

            {/* Pagination */}
            {totalPages > 1 && (
              <div className="mt-4 flex items-center justify-between border-t border-neutral-100 pt-4">
                <p className="text-sm text-neutral-500">
                  Page {page} of {totalPages}
                </p>
                <div className="flex items-center gap-2">
                  <button
                    onClick={() => setPage((p) => Math.max(1, p - 1))}
                    disabled={page === 1}
                    className="rounded-button p-1.5 text-neutral-500 hover:bg-neutral-100 disabled:opacity-40 transition"
                  >
                    <ChevronLeftIcon className="h-4 w-4" />
                  </button>
                  {Array.from({ length: Math.min(5, totalPages) }, (_, i) => {
                    const p = Math.max(1, Math.min(totalPages - 4, page - 2)) + i;
                    return (
                      <button
                        key={p}
                        onClick={() => setPage(p)}
                        className={clsx(
                          'h-7 w-7 rounded-button text-xs font-medium transition',
                          p === page
                            ? 'bg-primary-600 text-white'
                            : 'text-neutral-600 hover:bg-neutral-100'
                        )}
                      >
                        {p}
                      </button>
                    );
                  })}
                  <button
                    onClick={() => setPage((p) => Math.min(totalPages, p + 1))}
                    disabled={page === totalPages}
                    className="rounded-button p-1.5 text-neutral-500 hover:bg-neutral-100 disabled:opacity-40 transition"
                  >
                    <ChevronRightIcon className="h-4 w-4" />
                  </button>
                </div>
              </div>
            )}
          </>
        )}
      </Card>

      {/* Modals */}
      <QuestionFormModal
        isOpen={modalOpen}
        onClose={() => setModalOpen(false)}
        editTarget={editTarget}
        categories={categories}
        onSuccess={fetchQuestions}
      />
      <DeleteConfirmModal
        isOpen={deleteOpen}
        onClose={() => setDeleteOpen(false)}
        target={deleteTarget}
        onConfirm={fetchQuestions}
      />
    </div>
  );
}
